import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import {
  AuthActions,
  AuthField,
  AuthNotice,
  AuthPage,
  PasswordInput,
  SubmitButton,
  authKitTextKeys,
  showResult,
} from "../../auth/authKit";
import { completeReset } from "./accountApi";
import { useFocusHeading } from "./focusHeading";

/** Claves de texto de la pantalla (las que lee Recuperar.razor), el aviso de falla técnica y las del kit. */
export const recoverTextKeys = [
  ...authKitTextKeys,
  "RESET_TITLE",
  "RESET_SUBTITLE",
  "RESET_DONE_TITLE",
  "RESET_DONE_TEXT",
  "RESET_INVALID",
  "RESET_EMAIL",
  "RESET_NEW_PASSWORD",
  "RESET_SUBMIT",
  "ACCEPT_LOGIN",
  "CP_PASSWORD_RULE",
  "CP_AUTH_DATA_REJECTED",
  "ERROR_TEXT",
];

/**
 * Restablecer la contraseña con el enlace de un solo uso (/recuperar?token=, Recuperar.razor) con las piezas del kit. Sin
 * token, el enlace ya no sirve. Un rechazo deja el formulario con el correo escrito (la nueva clave se vacía); solo un
 * enlace vencido cambia a «enlace inválido».
 */
export function RecoverPage({ L }: { L: (key: string) => string }) {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(() => !token?.trim());
  const [error, setError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [sending, setSending] = useState(false);
  const password = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const title = L("RESET_TITLE");
  useEffect(() => {
    document.title = title;
  }, [title]);
  useFocusHeading(true);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setSending(true);
    try {
      const result = await completeReset({ token, email, newPassword: password.current?.value ?? "" });
      if (password.current) password.current.value = "";
      const onPassword = result.messageKey === "CP_PASSWORD_RULE";
      showResult(() => {
        if (result.status === "done") setDone(true);
        else if (result.status === "invalid") setInvalid(true);
        setPasswordError(onPassword ? L(result.messageKey!) : null);
        setError(result.messageKey && !onPassword ? L(result.messageKey) : null);
        setAttempt((value) => value + 1);
      }, onPassword);
    } finally {
      busy.current = false;
      setSending(false);
    }
  };

  return (
    <AuthPage title={title}>
      {done ? (
        <>
          <AuthNotice tone="done">{L("RESET_DONE_TITLE")}</AuthNotice>
          <p className="auth-intro">{L("RESET_DONE_TEXT")}</p>
          <AuthActions>
            <a className="tm-btn primary" href="/login">
              {L("ACCEPT_LOGIN")}
            </a>
          </AuthActions>
        </>
      ) : invalid ? (
        <AuthNotice tone="error">{L("RESET_INVALID")}</AuthNotice>
      ) : (
        <>
          <p className="auth-intro">{L("RESET_SUBTITLE")}</p>
          {error ? (
            <AuthNotice key={attempt} tone="error">
              {error}
            </AuthNotice>
          ) : null}
          <form method="post" onSubmit={(event) => void submit(event)}>
            <AuthField id="email" label={L("RESET_EMAIL")}>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                name="form.Email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </AuthField>
            <AuthField
              id="newPassword"
              label={L("RESET_NEW_PASSWORD")}
              error={passwordError}
              help={L("CP_PASSWORD_RULE")}
            >
              <PasswordInput
                id="newPassword"
                name="form.NewPassword"
                autoComplete="new-password"
                inputRef={password}
                error={passwordError}
                help={L("CP_PASSWORD_RULE")}
                required
                L={L}
              />
            </AuthField>
            <AuthActions>
              <SubmitButton sending={sending}>{L("RESET_SUBMIT")}</SubmitButton>
            </AuthActions>
          </form>
        </>
      )}
    </AuthPage>
  );
}
